import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, Bot, ShieldCheck, Languages, Zap, Users, PhoneCall,
  MessageSquare, ArrowRight, CheckCircle2, Lock, FileText, Brain,
  Radio, Building2, Stethoscope, Scale, Home, Car, Plane, Hotel,
  UtensilsCrossed, Wrench, School, GraduationCap, Heart, Compass,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FeatureInfographic from "@/components/FeatureInfographic";
import SectionInfographicGrid from "@/components/SectionInfographicGrid";
import { TalkToAiEmployeeButton } from "@/components/TalkToAiEmployeeLauncher";

const TILE = "rounded-2xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-md transition";
const TAG = "inline-flex items-center gap-1.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 px-3 py-1 text-xs font-semibold";

const INDUSTRIES = [
  { icon: Stethoscope, label: "Medical clinics", desc: "Explain services, collect intake, book visits." },
  { icon: Stethoscope, label: "Dentists", desc: "Procedure & insurance questions, appointment booking." },
  { icon: Scale, label: "Attorneys", desc: "Preliminary intake; never unauthorized legal advice." },
  { icon: Home, label: "Real estate", desc: "Qualify buyers, match properties, schedule viewings." },
  { icon: Car, label: "Automobile dealers", desc: "Match vehicles, explain financing, book test drives." },
  { icon: Plane, label: "Travel companies", desc: "Recommend trips, connect visitors with agents." },
  { icon: Hotel, label: "Hotels & resorts", desc: "Property questions and reservations." },
  { icon: UtensilsCrossed, label: "Restaurants & catering", desc: "Menu, dietary options, catering inquiries." },
  { icon: Wrench, label: "Plumbers / Electricians", desc: "Determine type, urgency, dispatch or schedule." },
  { icon: School, label: "Schools", desc: "Explain programs, admissions, book campus tours." },
  { icon: Compass, label: "Tourism portals", desc: "Multilingual destination guide + lead routing." },
  { icon: Heart, label: "Memorial services", desc: "Guide families compassionately through creation." },
];

const CAPABILITIES = [
  { icon: Brain, title: "Knowledge-grounded answers", desc: "Every reply is drawn from your approved website, documents and FAQs — never invented." },
  { icon: Languages, title: "Multilingual by default", desc: "Detects the visitor's language and responds in it. Verified translations sit above machine translations." },
  { icon: Users, title: "Business personality", desc: "Owner's voice, brand tone, allowed topics, prohibited claims — all client-controlled." },
  { icon: Zap, title: "Permission-based action engine", desc: "Book appointments, qualify leads, request callbacks, generate summaries. Only what you authorize." },
  { icon: Radio, title: "Web · Voice · Phone · WhatsApp · SMS", desc: "One brain across every channel. Optional avatar for the AI Business Twin tier." },
  { icon: ShieldCheck, title: "Consent, disclosure & audit", desc: "AI disclosure, recording consent, PII redaction, retention control, full audit trail." },
];

const PACKAGES = [
  {
    key: "text", name: "AI Website Representative", price: "Package A",
    highlight: false,
    features: [
      "Knowledge-grounded text chat",
      "Business personality & brand voice",
      "Visitor-intent detection",
      "Lead qualification",
      "Appointment & callback requests",
      "Human handoff",
      "Conversation summaries",
      "Multilingual text",
      "Basic analytics",
      "Approved calls to action",
    ],
  },
  {
    key: "voice", name: "AI Voice Representative", price: "Package B",
    highlight: true,
    features: [
      "Everything in Package A",
      "Browser-based voice conversations",
      "Speech-to-text and text-to-speech",
      "Telephone answering",
      "Call summaries & disposition",
      "Callback scheduling",
      "Voicemail handling",
      "SMS & WhatsApp continuation",
      "Automatic language detection",
    ],
  },
  {
    key: "twin", name: "AI Business Twin", price: "Package C",
    highlight: false,
    features: [
      "Everything in Packages A & B",
      "Client-approved visual representative",
      "Photograph or animated presenter",
      "Optional consent-based cloned voice",
      "Multiple authorized staff twins",
      "Video greetings & service explanations",
      "Advanced personality controls",
      "Higher-level analytics & optimization",
    ],
  },
];

const FLOW_STEPS = [
  { n: "1", title: "Visitor arrives", desc: "Website, phone, SMS or WhatsApp — one brain across all of them." },
  { n: "2", title: "Understands the need", desc: "Detects language, intent and urgency in the first message." },
  { n: "3", title: "Answers from approved knowledge", desc: "Cites your website, documents, FAQs — never invents facts." },
  { n: "4", title: "Recommends next action", desc: "Book, quote, transfer, callback — only actions you authorized." },
  { n: "5", title: "Converts or hands off cleanly", desc: "Full transcript, summary and context handed to a human." },
];

export default function AiRepresentativePage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [demoMsg, setDemoMsg] = useState("");

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1" data-testid="ai-representative-page">
        {/* Hero */}
        <section className="bg-gradient-to-b from-indigo-950 via-slate-900 to-slate-950 text-white pt-28 pb-20">
          <div className="max-w-6xl mx-auto px-6">
            <span className={`${TAG} bg-indigo-900/50 border-indigo-700/50 text-indigo-200`} data-testid="rep-hero-badge">
              <Sparkles className="w-3.5 h-3.5" /> Now available · Package A live
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-4xl">
              WebsiteTech AI Representative
            </h1>
            <p className="mt-3 text-lg text-indigo-300 italic">
              Expert Presence &nbsp;·&nbsp; Digital Front Desk &nbsp;·&nbsp; Live Presence
            </p>
            <p className="mt-6 text-lg text-slate-300 max-w-2xl">
              Not a chatbot. A knowledgeable, business-specific digital representative
              that answers from your approved knowledge, qualifies leads,
              takes appointments, and hands off to a human — in any language,
              on any channel.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/representative"
                data-testid="hero-configure-btn"
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-500 hover:bg-indigo-400 px-5 py-3 font-semibold transition"
              >
                Configure your representative <ArrowRight className="w-4 h-4" />
              </Link>
              <TalkToAiEmployeeButton size="md" variant="ghost" />
              <Link
                to="/audit"
                data-testid="hero-demo-btn"
                className="inline-flex items-center gap-2 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 px-5 py-3 font-semibold transition"
              >
                Get a free website audit
              </Link>
            </div>
            <p className="mt-6 text-xs text-slate-400 max-w-2xl">
              Every representative discloses that it is AI. Every voice or
              likeness requires signed consent. Every reply is grounded and
              auditable. Not a certification or replacement for licensed
              professionals.
            </p>
          </div>
        </section>

        {/* Flow */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-base md:text-lg font-bold uppercase tracking-widest text-indigo-700">
              How it works
            </h2>
            <p className="mt-2 text-3xl font-bold text-slate-900 max-w-3xl">
              A single trusted business brain — served through every channel.
            </p>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-5 gap-4">
              {FLOW_STEPS.map((s) => (
                <div key={s.n} className="relative rounded-xl border border-slate-200 p-5 bg-slate-50" data-testid={`flow-step-${s.n}`}>
                  <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-indigo-600 text-white grid place-items-center font-bold text-sm">{s.n}</div>
                  <div className="text-sm font-semibold text-slate-900 mt-1">{s.title}</div>
                  <div className="mt-2 text-xs text-slate-600 leading-relaxed">{s.desc}</div>
                </div>
              ))}
            </div>

            {/* AI-generated infographic */}
            <div className="mt-14">
              <FeatureInfographic slug="representative" accent="#4F46E5" />
              <SectionInfographicGrid slug="representative" accent="#4F46E5" />
            </div>
          </div>
        </section>

        {/* Capabilities */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-slate-900">Built for real business work</h2>
            <p className="mt-2 text-slate-600 max-w-2xl">
              A generic chatbot converses. Your Representative closes work.
            </p>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {CAPABILITIES.map((c) => (
                <div key={c.title} className={TILE} data-testid={`capability-${c.title.split(' ')[0].toLowerCase()}`}>
                  <c.icon className="w-6 h-6 text-indigo-600" />
                  <div className="mt-4 font-semibold text-slate-900">{c.title}</div>
                  <div className="mt-2 text-sm text-slate-600 leading-relaxed">{c.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Industries */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-slate-900">Prebuilt for 13 industries</h2>
            <p className="mt-2 text-slate-600 max-w-2xl">
              Each template ships with the right tone, refused topics,
              actions, qualification questions and disclosures baked in —
              editable at any time.
            </p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {INDUSTRIES.map((it) => (
                <div key={it.label} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid={`industry-${it.label.split(' ')[0].toLowerCase()}`}>
                  <div className="w-9 h-9 rounded-lg bg-white grid place-items-center border border-slate-200 flex-shrink-0">
                    <it.icon className="w-4 h-4 text-indigo-600" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{it.label}</div>
                    <div className="mt-1 text-xs text-slate-600">{it.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Safety */}
        <section className="py-20 bg-slate-900 text-white">
          <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-10 items-start">
            <div>
              <span className={`${TAG} bg-emerald-900/50 border-emerald-700/50 text-emerald-200`}>
                <ShieldCheck className="w-3.5 h-3.5" /> Consent-first by design
              </span>
              <h2 className="mt-4 text-3xl font-bold">Safety, identity & control — not an afterthought</h2>
              <p className="mt-4 text-slate-300">
                Cloning a voice or likeness requires a signed, dated,
                revocable consent. Restricted topics (medical, legal,
                financial) route to humans by policy. Every conversation is
                auditable; sensitive data can be redacted from stored
                transcripts.
              </p>
            </div>
            <div className="grid gap-3">
              {[
                ["Verified authority", "Client proves authority before a representative goes live."],
                ["Voice / likeness consent", "Signed, revocable — instant deactivation on withdrawal."],
                ["AI disclosure & recording notice", "Visible on every channel."],
                ["PII redaction & retention", "Emails, phones, cards, SSN patterns masked in stored transcripts."],
                ["Restricted-topic guardrails", "Medical, legal, financial → disclaimer + human handoff."],
                ["Audit trail", "Every knowledge change, prompt change, action executed."],
              ].map((row) => (
                <div key={row[0]} className="flex gap-3 items-start rounded-xl border border-white/10 bg-white/5 p-4" data-testid={`safety-${row[0].split(' ')[0].toLowerCase()}`}>
                  <Lock className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-sm font-semibold">{row[0]}</div>
                    <div className="mt-1 text-xs text-slate-400">{row[1]}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Packages */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-slate-900">Three ways to represent your business</h2>
            <p className="mt-2 text-slate-600 max-w-2xl">
              Start with text. Add voice when ready. Move to a full digital
              twin only when it genuinely helps — not because it's flashy.
            </p>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
              {PACKAGES.map((p) => (
                <div
                  key={p.key}
                  data-testid={`package-${p.key}`}
                  className={`rounded-2xl border p-6 flex flex-col ${p.highlight ? "border-indigo-500 bg-indigo-50 ring-1 ring-indigo-200" : "border-slate-200 bg-white"}`}
                >
                  {p.highlight && <div className="text-xs font-bold uppercase tracking-widest text-indigo-600 mb-2">Most popular</div>}
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{p.price}</div>
                  <div className="mt-1 text-lg font-bold text-slate-900">{p.name}</div>
                  <ul className="mt-5 space-y-2 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                        <CheckCircle2 className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/representative"
                    data-testid={`package-${p.key}-cta`}
                    className={`mt-6 inline-flex items-center justify-center gap-2 rounded-lg px-4 py-3 font-semibold transition ${p.highlight ? "bg-indigo-600 text-white hover:bg-indigo-500" : "bg-slate-900 text-white hover:bg-slate-800"}`}
                  >
                    Configure {p.name.split(' ')[1] || 'now'} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 bg-gradient-to-b from-indigo-950 to-slate-950 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <Bot className="w-12 h-12 text-indigo-400 mx-auto" />
            <h2 className="mt-6 text-3xl md:text-4xl font-bold">
              Improve the website. Convert more visitors. Prove measurable results.
            </h2>
            <p className="mt-4 text-slate-300 max-w-2xl mx-auto">
              Your Representative is a permanent front line — always on,
              always grounded, always accountable.
            </p>
            <Link
              to="/representative"
              data-testid="final-cta-btn"
              className="mt-8 inline-flex items-center gap-2 rounded-lg bg-indigo-500 hover:bg-indigo-400 px-6 py-4 font-semibold text-lg transition"
            >
              Start configuring — 5 minutes <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
